import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { ParceirosStatusBadge } from '@/pages/parceiros/ParceirosStatusBadge'
import { indicadorTipoLabel } from '@/pages/indicacoes/indicadorTipos'
import { indicadorDaLinha, comercialDaLinha, type IndicacaoStaffRow } from '@/pages/indicacoes/indicacoesFilters'
import { podeDescartar, type IndicacaoAba } from '@/pages/indicacoes/indicacaoAbas'
import { formatPhone } from '@/utils/formatPhone'

function siteLink(site: string) {
  const text = site.trim()
  if (!text || /^(javascript|data|vbscript):/i.test(text)) return null
  if (/^https?:\/\//i.test(text)) return text
  return `https://${text}`
}

const EMPTY: Record<IndicacaoAba, string> = {
  abertas: 'Nenhuma indicação em aberto.',
  descartados: 'Nenhum lead descartado.',
  nao_fecharam: 'Nenhum lead que não fechou negócio.',
}

type Props = {
  rows: IndicacaoStaffRow[]
  aba: IndicacaoAba
  hasFilters: boolean
  page: number
  pageCount: number
  pageSize: number
  filteredCount: number
  userId?: string
  role?: string
  showComercial?: boolean
  sendingId: string | null
  discardingId: string | null
  onPage: (page: number) => void
  onCarteira: (row: IndicacaoStaffRow) => void
  onDiscard: (row: IndicacaoStaffRow) => void
}

function naCarteira(row: IndicacaoStaffRow, userId?: string, role?: string) {
  const instance = row.leadInstance
  if (!instance || instance.deletedAt || !instance.closerId) return false
  if (role === 'closer') return instance.closerId === userId
  return true
}

export function IndicacoesTable({
  rows,
  aba,
  hasFilters,
  page,
  pageCount,
  pageSize,
  filteredCount,
  userId,
  role,
  showComercial = false,
  sendingId,
  discardingId,
  onPage,
  onCarteira,
  onDiscard,
}: Props) {
  const headers = ['Cliente', 'Contato', 'Indicador', ...(showComercial ? ['Comercial'] : []), 'Tipo', 'Site', 'Status', 'Ações']
  return (
    <div className='glass-card overflow-hidden p-0'>
      <table className='w-full text-left text-sm'>
        <thead className='border-b border-[var(--glass-border)] text-[var(--font-color-label)]'>
          <tr>
            {headers.map((header) => (
              <th
                key={header}
                className={`px-4 py-3 text-xs font-medium uppercase tracking-wider${header === 'Ações' ? ' text-right' : ''}`}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={headers.length} className='px-4 py-10 text-center text-[var(--font-color-muted)]'>
                {hasFilters ? 'Nenhuma indicação encontrada.' : EMPTY[aba]}
              </td>
            </tr>
          ) : rows.map((row) => {
            const href = row.site ? siteLink(row.site) : null
            return (
            <tr key={row.id} className='border-b border-[var(--glass-border)] last:border-0 transition-colors hover:bg-[var(--glass-bg)]'>
              <td className='px-4 py-3'>
                <p className='font-medium text-[var(--font-color-body)]'>{row.nome}</p>
                <p className='text-xs text-[var(--font-color-muted)]'>{row.empresa}</p>
              </td>
              <td className='px-4 py-3'>
                {row.telefone ? (
                  <a className='font-medium text-[var(--font-color-body)] hover:text-accent' href={`tel:${row.telefone.replace(/\D/g, '')}`}>
                    {formatPhone(row.telefone)}
                  </a>
                ) : (
                  <span className='text-[var(--font-color-muted)]'>—</span>
                )}
                <p className='text-xs text-[var(--font-color-muted)]'>
                  {row.email ? (
                    <a className='hover:text-accent' href={`mailto:${row.email}`}>{row.email}</a>
                  ) : 'Sem e-mail'}
                </p>
              </td>
              <td className='px-4 py-3 text-[var(--font-color-body)]'>{indicadorDaLinha(row) || '—'}</td>
              {showComercial && (
                <td className={`px-4 py-3 ${comercialDaLinha(row) ? 'text-[var(--font-color-body)]' : 'text-[var(--font-color-muted)]'}`}>
                  {comercialDaLinha(row) || 'Sem comercial'}
                </td>
              )}
              <td className='px-4 py-3 text-[var(--font-color-muted)]'>{indicadorTipoLabel(row.referrer?.tipo)}</td>
              <td className='px-4 py-3 max-w-[220px]'>
                {href ? (
                  <a className='break-all text-xs text-accent hover:underline' href={href} target='_blank' rel='noreferrer'>
                    {row.site}
                  </a>
                ) : (
                  <span className='text-[var(--font-color-muted)]'>—</span>
                )}
              </td>
              <td className='px-4 py-3'>
                {row.leadInstance?.status === 'DESCARTADO'
                  ? <Badge variant='danger'>Descartado</Badge>
                  : <ParceirosStatusBadge status={row.publicStatus} />}
              </td>
              <td className='px-4 py-3'>
                <div className='flex items-center justify-end gap-2'>
                  {naCarteira(row, userId, role) ? (
                    <Link
                      className='inline-flex h-11 w-44 shrink-0 items-center justify-center rounded-md border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 text-xs font-medium text-accent'
                      to='/closer-leads'
                    >
                      Na carteira
                    </Link>
                  ) : (
                    <Button
                      type='button'
                      variant='secondary'
                      size='sm'
                      className='h-11 w-44 shrink-0 justify-center px-3'
                      disabled={sendingId === row.id}
                      onClick={() => onCarteira(row)}
                    >
                      {sendingId === row.id ? 'Enviando…' : 'Enviar para carteira'}
                    </Button>
                  )}
                  {podeDescartar(row) && (
                    <Button
                      type='button'
                      variant='destructive'
                      size='sm'
                      className='h-11 w-44 shrink-0 justify-center px-3'
                      disabled={discardingId === row.id}
                      onClick={() => onDiscard(row)}
                    >
                      {discardingId === row.id ? 'Descartando…' : 'Descartar'}
                    </Button>
                  )}
                </div>
              </td>
            </tr>
            )
          })}
        </tbody>
      </table>
      {pageCount > 1 && (
        <div className='flex items-center justify-between border-t border-[var(--glass-border)] px-4 py-3 text-sm text-[var(--font-color-muted)]'>
          <span>
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filteredCount)} de {filteredCount}
          </span>
          <div className='flex gap-2'>
            <Button type='button' variant='ghost' size='sm' disabled={page <= 1} onClick={() => onPage(page - 1)}>
              Anterior
            </Button>
            <Button type='button' variant='ghost' size='sm' disabled={page >= pageCount} onClick={() => onPage(page + 1)}>
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
